import { Link } from 'react-router-dom';
import type { Article } from '@/mocks/articles';

interface ArticleCardProps {
  article: Article;
  variant?: 'default' | 'horizontal';
}

export default function ArticleCard({ article, variant = 'default' }: ArticleCardProps) {
  if (variant === 'horizontal') {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="group flex flex-col sm:flex-row gap-4 p-4 rounded-lg bg-background-50 hover:bg-background-100 transition-colors cursor-pointer"
      >
        <div className="w-full sm:w-40 h-48 sm:h-28 flex-shrink-0 rounded-md overflow-hidden">
          <img
            src={article.coverImage}
            alt={article.title}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
          />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="text-xs px-2 py-0.5 rounded-full bg-accent-100 text-accent-700 whitespace-nowrap">
              {article.character}
            </span>
            <span className="text-xs text-foreground-500 whitespace-nowrap">{article.readTime}</span>
          </div>
          <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-2 mb-1">
            {article.title}
          </h3>
          <p className="text-xs text-foreground-500 line-clamp-2">{article.excerpt}</p>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.slug}`}
      className="group block rounded-lg overflow-hidden bg-background-50 hover:bg-background-100 transition-all cursor-pointer"
    >
      <div className="relative aspect-[3/4] overflow-hidden">
        <img
          src={article.coverImage}
          alt={article.title}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
        {article.featured && (
          <span className="absolute top-3 left-3 px-2 py-0.5 rounded-full text-xs font-medium bg-accent-500 text-background-50 whitespace-nowrap">
            精选
          </span>
        )}
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-3">
          <span className="text-xs text-white/90">{article.readTime}</span>
        </div>
      </div>
      <div className="p-4">
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <span className="text-xs px-2 py-0.5 rounded-full bg-accent-100 text-accent-700 whitespace-nowrap">
            {article.character}
          </span>
          <span className="text-xs text-foreground-400 whitespace-nowrap">{article.author}</span>
        </div>
        <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-2 mb-1">
          {article.title}
        </h3>
        <p className="text-xs text-foreground-500 line-clamp-2">{article.excerpt}</p>
        <div className="flex items-center gap-1 mt-3 flex-wrap">
          {article.tags.slice(0, 2).map((tag) => (
            <span key={tag} className="text-xs text-foreground-400 whitespace-nowrap">
              #{tag}
            </span>
          ))}
        </div>
      </div>
    </Link>
  );
}